import React, { useState } from 'react';
import { format, isSameDay, parseISO } from 'date-fns';
import { useCalendar } from '../../contexts/CalendarContext';
import { useNavigate } from 'react-router-dom';
import './Calendar.css';
// Import our custom event component
import { DayEvent } from './EventComponent';

const DayView = ({ onEventClick }) => {
  const { 
    selectedDate,
    events
  } = useCalendar();
  
  const navigate = useNavigate();
  const [tooltipEvent, setTooltipEvent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  // Get events for the selected day based on start_date
  const getEventsForSelectedDay = () => {
    if (!events || !Array.isArray(events)) return [];
    
    return events.filter(event => {
      // Get the date from start_date field
      const eventStartDate = event.start_date ? parseISO(event.start_date) : 
                           (event.start ? parseISO(event.start) : null);
      
      // If no date is available, skip this event
      if (!eventStartDate) return false;
      
      // Check if the event's date matches the selected date
      return isSameDay(eventStartDate, selectedDate);
    });
  };
  
  const dayEvents = getEventsForSelectedDay();
  
  // Helper function to position events - ignore time and just stack them by date
  const positionEvent = (event, index) => {
    // Ignore the actual time, just stack events vertically based on their index
    // This creates a list-like view of events without time positioning
    const top = 10 + (index * 185); // 185px height per event with a small gap
    
    return {
      top: `${top}px`,
      height: `180px`, // Fixed height for each event
      position: 'relative'
    };
  };
  
  const handleEventClick = (event, e) => {
    e.stopPropagation();
    
    // If provided, use the parent component's event handler
    if (onEventClick) {
      onEventClick(event);
    } else {
      // Default behavior - navigate to the booking details page
      navigate(`/admin/detail?id=${event.id}`);
    }
  };
  
  const showTooltip = (event, e) => {
    e.stopPropagation();
    
    // Set the position relative to the cursor
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + window.scrollY - 5,
      left: rect.left + window.scrollX + rect.width + 5
    });
    
    setTooltipEvent(event);
  };
  
  const hideTooltip = () => {
    setTooltipEvent(null);
  };
  
  return (
    <div className="calendar-day-view">
      
      <div className="calendar-day-grid">
        {/* Time column */}
        <div className="calendar-time-column">
          {hours.map(hour => (
            <div key={hour} className="calendar-time-slot">
              <span>{format(new Date().setHours(hour, 0, 0, 0), 'h a')}</span>
            </div>
          ))}
        </div>
        
        {/* Day content */}
        <div className="calendar-day-content">
          {hours.map(hour => (
            <div key={hour} className="calendar-hour-slot"></div>
          ))}
          
          {/* Events */}
          {dayEvents.length > 0 ? (
            dayEvents.map((event, index) => (
              <div key={event.id} className="calendar-day-event-wrapper">
                <DayEvent 
                  event={event}
                  onEventClick={onEventClick}
                />
              </div>
            ))
          ) : (
            <div className="calendar-no-events">No events for this day</div>
          )}
        </div>
      </div>
      
      {tooltipEvent && (
        <div 
          className="calendar-event-tooltip"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`
          }}
        >
          <div className="calendar-event-tooltip-title">
            {tooltipEvent.title}
          </div>
          
          {tooltipEvent.client && (
            <div className="calendar-tooltip-field">
              <span className="calendar-tooltip-label">Client: </span>
              <span className="calendar-tooltip-value">{typeof tooltipEvent.client === 'object' ? (tooltipEvent.client.label || tooltipEvent.client.name || 'N/A') : (tooltipEvent.client || 'N/A')}</span>
            </div>
          )}
          
          {tooltipEvent.talent && (
            <div className="calendar-tooltip-field">
              <span className="calendar-tooltip-label">Talent: </span>
              <span className="calendar-tooltip-value">{tooltipEvent.talent}</span>
            </div>
          )}
          
          {tooltipEvent.position && (
            <div className="calendar-tooltip-field">
              <span className="calendar-tooltip-label">Position: </span>
              <span className="calendar-tooltip-value">{tooltipEvent.position}</span>
            </div>
          )}
          
          {tooltipEvent.status && (
            <div className="calendar-tooltip-field">
              <span className="calendar-tooltip-label">Status: </span>
              <span className="calendar-tooltip-value">{tooltipEvent.status}</span>
            </div>
          )}
          
          {tooltipEvent.startTime && tooltipEvent.endTime && (
            <div className="calendar-tooltip-field">
              <span className="calendar-tooltip-label">Time: </span>
              <span className="calendar-tooltip-value">
                {tooltipEvent.startTime} - {tooltipEvent.endTime}
              </span>
            </div>
          )}
          
          {tooltipEvent.location && (
            <div className="calendar-tooltip-field">
              <span className="calendar-tooltip-label">Location: </span>
              <span className="calendar-tooltip-value">{tooltipEvent.location}</span>
            </div>
          )}
          
          {tooltipEvent.set && (
            <div className="calendar-tooltip-field">
              <span className="calendar-tooltip-label">Set: </span>
              <span className="calendar-tooltip-value">{tooltipEvent.set}</span>
            </div>
          )}
          
          {tooltipEvent.proposedBy && (
            <div className="calendar-tooltip-field">
              <span className="calendar-tooltip-label">Proposed By: </span>
              <span className="calendar-tooltip-value">{tooltipEvent.proposedBy}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DayView; 